import React, { useState, useEffect } from 'react';
import { Building2, Plus, Globe, Mail, Shield, CreditCard, Code, MoreHorizontal, Settings2, Loader2, X, Check, Eye, Pause, Trash2, Map as MapIcon, Upload, FileSpreadsheet, AlertCircle, ArrowRight, Table, Layers, AlertTriangle, Lock, Unlock, PlayCircle, BarChart3, TrendingUp, Activity, Edit2, Database, Users, Save, GitPullRequest, ArrowUpRight, Ban, UserPlus, Send, CheckCircle2, Ticket, Gift, PieChart, Calendar, RefreshCw, ChevronDown, QrCode, Barcode, FlaskConical, Sparkles, Copy, Download, UserCheck, Terminal, Clock, Braces, ChevronUp, History, Network, User, Info, Zap, MessageSquare, HardDrive } from 'lucide-react';
import { Container, UserRole, Institution, PLAN_LIMITS, PlanRequest, User as UserType, EmailOverride } from '../types';
import { supabase } from '../supabase';
import MapModule from './MapModule';
import SuperAdminSupportPanel from './SuperAdminSupportPanel';

interface Coupon {
    id: string;
    code: string;
    type: 'percent' | 'fixed';
    value: number;
    max_redemptions?: number;
    expires_at?: string;
    is_active: boolean;
    applies_to: 'monthly' | 'yearly' | 'both';
    apply_mode: 'first_payment' | 'all_payments';
    _count_redemptions?: number;
    _sum_discount?: number;
}

interface PendingRequest {
  userId: string;
  userName: string;
  userEmail: string;
  cabinetName: string;
  slug: string;
  plan: 'essential' | 'advanced' | 'pro' | 'betatester';
  requestedAt: string;
  billingInfo: any;
  paymentId: string;
  isOverride?: boolean;
  expiresAt?: string;
}

interface SuperAdminDashboardProps {
  containers: Container[];
  currentUser: UserType;
  onUpdateContainers: () => void;
  onViewContainer: (containerId: string) => void;
}

const SuperAdminDashboard: React.FC<SuperAdminDashboardProps> = ({ containers, currentUser, onUpdateContainers, onViewContainer }) => {
  const [activeTab, setActiveTab] = useState<'instances' | 'requests' | 'map' | 'coupons' | 'overrides' | 'support' | 'users' | 'metrics'>('instances');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [emailOverrides, setEmailOverrides] = useState<EmailOverride[]>([]);
  const [showCreateCoupon, setShowCreateCoupon] = useState(false);
  const [showCreateOverride, setShowCreateOverride] = useState(false);
  
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [editingCabinet, setEditingCabinet] = useState<Container | null>(null);
  const [statusMsg, setStatusMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // States for new tabs
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [containerMetrics, setContainerMetrics] = useState<any[]>([]);

  useEffect(() => {
    if (activeTab === 'coupons') fetchCoupons();
    if (activeTab === 'overrides') fetchOverrides();
    if (activeTab === 'requests') fetchPendingRequests();
    if (activeTab === 'users') fetchAllUsers();
    if (activeTab === 'metrics') fetchMetrics();
  }, [activeTab]);

  const fetchOverrides = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('email_overrides').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setEmailOverrides(data || []);
    } catch (e: any) {
      console.error(e);
      setStatusMsg({ type: 'error', text: 'Erro ao carregar overrides.' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, name, email, phone, role, container_id, containers(name)')
        .order('created_at', { ascending: false });
      if (!error && data) setAllUsers(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      // Tenta usar a view otimizada
      const { data, error } = await supabase.from('view_container_stats').select('*');
      
      if (!error && data) {
        setContainerMetrics(data);
      } else {
        // Fallback: busca containers e conta um a um (lento, mas seguro se a view não existir ainda)
        const metrics = [];
        for (const c of containers) {
           const { count: contactsCount } = await supabase.from('contacts').select('*', { count: 'exact', head: true }).eq('container_id', c.id).is('deleted_at', null);
           const { count: usersCount } = await supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('container_id', c.id);
           
           metrics.push({
             container_id: c.id,
             container_name: c.name,
             contacts_count: contactsCount || 0,
             users_count: usersCount || 0,
             storage_used_bytes: 0 
           });
        }
        setContainerMetrics(metrics);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleCreateOverride = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.currentTarget);
    try {
      const { error } = await supabase.from('email_overrides').insert([{
        email: String(formData.get('email')).toLowerCase().trim(),
        duration_days: Number(formData.get('duration_days')),
        created_by: currentUser.id
      }]);
      if (error) throw error;
      setStatusMsg({ type: 'success', text: 'Override concedido!' });
      setShowCreateOverride(false);
      fetchOverrides();
    } catch (e: any) {
      setStatusMsg({ type: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOverride = async (id: string) => {
    if (!confirm("Remover este privilégio de override?")) return;
    setActionLoading(id);
    try {
      const { error } = await supabase.from('email_overrides').delete().eq('id', id);
      if (error) throw error;
      fetchOverrides();
    } catch (e: any) {
      setStatusMsg({ type: 'error', text: e.message });
    } finally {
      setActionLoading(null);
    }
  };

  const fetchPendingRequests = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, name, email, settings')
        .not('settings->pending_cabinet_request', 'is', null);
      
      if (error) throw error;

      const formatted: PendingRequest[] = (data || [])
        .filter(u => u.settings && typeof u.settings === 'object' && u.settings.pending_cabinet_request)
        .map((u: any) => {
          const req = u.settings?.pending_cabinet_request;
          return {
            userId: u.id,
            userName: u.name || 'Usuário Sem Nome',
            userEmail: u.email || '',
            cabinetName: req?.name || 'Gabinete Indefinido',
            slug: req?.slug || '',
            plan: req?.plan || 'essential',
            requestedAt: req?.requested_at || new Date().toISOString(),
            billingInfo: req?.billing_info || {},
            paymentId: req?.payment_id || '',
            isOverride: req?.is_override || false,
            expiresAt: req?.expires_at || null
          };
        });

      setPendingRequests(formatted);
    } catch (e: any) {
      setStatusMsg({ type: 'error', text: 'Erro ao buscar solicitações: ' + e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveCabinet = async (req: PendingRequest) => {
    setActionLoading(req.userId);
    try {
      const containerPayload: Record<string, any> = {
        name: req.cabinetName,
        slug: req.slug,
        admin_email: req.userEmail,
        plan: req.plan,
        status: 'active',
        billing_cycle: 'monthly',
      };
      if (req.expiresAt) containerPayload.expires_at = req.expiresAt;

      const { data: newContainer, error: containerErr } = await supabase
        .from('containers')
        .insert([containerPayload])
        .select()
        .single();

      if (containerErr) throw containerErr;

      const { error: profileErr } = await supabase
        .from('profiles')
        .update({
          container_id: newContainer.id,
          role: UserRole.ADMIN,
          settings: {} 
        })
        .eq('id', req.userId);

      if (profileErr) throw profileErr;

      setStatusMsg({ type: 'success', text: `Gabinete "${req.cabinetName}" ativado com sucesso!` });
      fetchPendingRequests();
      onUpdateContainers();
    } catch (e: any) {
      setStatusMsg({ type: 'error', text: 'Erro na ativação manual: ' + e.message });
    } finally {
      setActionLoading(null);
    }
  };

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const { data: couponsData, error } = await supabase
        .from('coupons')
        .select('*, coupon_redemptions(discount_snapshot)')
        .order('created_at', { ascending: false });
      
      if (error) throw error;

      if (couponsData) {
        const formatted = couponsData.map((c: any) => {
          const redemptions = c.coupon_redemptions || [];
          const sumDiscount = redemptions.reduce((acc: number, r: any) => acc + (r.discount_snapshot?.calculated_discount || 0), 0);
          return { ...c, _count_redemptions: redemptions.length, _sum_discount: sumDiscount };
        });
        setCoupons(formatted);
      }
    } catch (e: any) { 
      console.error(e);
      setStatusMsg({ type: 'error', text: 'Erro ao carregar cupons.' });
    } finally { 
      setLoading(false); 
    }
  };

  const handleCreateCoupon = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.currentTarget);
    try {
      const { error } = await supabase.from('coupons').insert([{
        code: String(formData.get('code')).toUpperCase(),
        type: formData.get('type'),
        value: Number(formData.get('value')),
        max_redemptions: formData.get('max_redemptions') ? Number(formData.get('max_redemptions')) : null,
        expires_at: formData.get('expires_at') || null,
        is_active: true,
        applies_to: 'both',
        apply_mode: 'first_payment',
        created_by: currentUser.id
      }]);
      if (error) throw error;
      setStatusMsg({ type: 'success', text: 'Cupom criado!' });
      setShowCreateCoupon(false);
      fetchCoupons();
    } catch (e: any) {
      setStatusMsg({ type: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCoupon = async (id: string, currentStatus: boolean) => {
    setActionLoading(id);
    try {
      const { error } = await supabase.from('coupons').update({ is_active: !currentStatus }).eq('id', id);
      if (error) throw error;
      fetchCoupons();
    } catch (e: any) {
      setStatusMsg({ type: 'error', text: e.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteCoupon = async (id: string) => {
    if (!confirm("Excluir este cupom permanentemente?")) return;
    setActionLoading(id);
    try {
      const { error } = await supabase.from('coupons').delete().eq('id', id);
      if (error) throw error;
      fetchCoupons();
    } catch (e: any) {
      setStatusMsg({ type: 'error', text: e.message });
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateCabinetPlan = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingCabinet) return;
    const formData = new FormData(e.currentTarget);
    setLoading(true);
    try {
      const { error } = await supabase.from('containers').update({
        plan: formData.get('plan'),
        billing_cycle: formData.get('billing_cycle'),
        admin_email: formData.get('admin_email'),
        status: formData.get('status')
      }).eq('id', editingCabinet.id);
      if (error) throw error;
      setStatusMsg({ type: 'success', text: 'Instância atualizada!' });
      setEditingCabinet(null);
      onUpdateContainers();
    } catch (err: any) { setStatusMsg({ type: 'error', text: err.message }); } finally { setLoading(false); }
  };

  const handleToggleStatus = async (id: string, currentStatus: string) => {
    const nextStatus = currentStatus === 'active' ? 'suspended' : 'active';
    setActionLoading(id);
    try {
      const { error } = await supabase.from('containers').update({ status: nextStatus }).eq('id', id);
      if (error) throw error;
      onUpdateContainers();
    } catch (err: any) { setStatusMsg({ type: 'error', text: err.message }); } finally { setActionLoading(null); }
  };

  return (
    <div className="max-w-7xl mx-auto animate-in fade-in duration-500 pb-20 relative">
      {statusMsg && (
        <div className={`fixed top-10 right-10 z-[250] flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl animate-in slide-in-from-right-4 ${statusMsg.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
           <span className="text-sm font-black uppercase tracking-widest">{statusMsg.text}</span>
           <button onClick={() => setStatusMsg(null)} className="ml-4 opacity-50 hover:opacity-100"><X size={16}/></button>
        </div>
      )}

      {/* MODAL NOVO OVERRIDE */}
      {showCreateOverride && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-navy-950/80 backdrop-blur-md animate-in fade-in">
           <form onSubmit={handleCreateOverride} className="bg-white dark:bg-navy-900 w-full max-w-lg rounded-[32px] shadow-2xl p-10 border border-neutral-200 dark:border-navy-800">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-2xl font-black text-navy-950 dark:text-white tracking-tight">Conceder Override</h3>
                <button type="button" onClick={() => setShowCreateOverride(false)} className="p-2 text-slate-400 hover:bg-slate-50 rounded-full"><X size={24}/></button>
              </div>
              <div className="space-y-6">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">E-mail do Beneficiário</label>
                    <input name="email" type="email" required placeholder="exemplo@gmail.com" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-2xl px-6 py-4 font-bold text-sm outline-none dark:text-white" />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Duração da Cortesia (Dias)</label>
                    <input name="duration_days" type="number" min="1" required placeholder="Ex: 30" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-2xl px-6 py-4 font-bold text-sm outline-none dark:text-white" />
                 </div>
                 <button disabled={loading} type="submit" className="w-full py-5 bg-navy-950 dark:bg-parliament-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl flex items-center justify-center gap-2">
                    {loading ? <Loader2 className="animate-spin" size={20}/> : <><Zap size={20}/> CONCEDER ACESSO</>}
                 </button>
              </div>
           </form>
        </div>
      )}

      {/* MODAL NOVO CUPOM */}
      {showCreateCoupon && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-navy-950/80 backdrop-blur-md animate-in fade-in">
           <form onSubmit={handleCreateCoupon} className="bg-white dark:bg-navy-900 w-full max-w-lg rounded-[32px] shadow-2xl p-10 border border-neutral-200 dark:border-navy-800">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-2xl font-black text-navy-950 dark:text-white tracking-tight">Novo Cupom</h3>
                <button type="button" onClick={() => setShowCreateCoupon(false)} className="p-2 text-slate-400 hover:bg-slate-50 rounded-full"><X size={24}/></button>
              </div>
              <div className="space-y-6">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Código do Cupom</label>
                    <input name="code" required placeholder="EX: VERAO20" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-2xl px-6 py-4 font-bold text-sm outline-none dark:text-white" />
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Tipo</label>
                        <select name="type" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-2xl px-4 py-4 font-bold text-sm outline-none dark:text-white">
                            <option value="percent">Percentual (%)</option>
                            <option value="fixed">Fixo (R$)</option>
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Valor</label>
                        <input name="value" type="number" required placeholder="0" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-2xl px-6 py-4 font-bold text-sm outline-none dark:text-white" />
                    </div>
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Limite de Usos (Opcional)</label>
                    <input name="max_redemptions" type="number" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-2xl px-6 py-4 font-bold text-sm outline-none dark:text-white" />
                 </div>
                 <button disabled={loading} type="submit" className="w-full py-5 bg-navy-950 dark:bg-parliament-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl flex items-center justify-center gap-2">
                    {loading ? <Loader2 className="animate-spin" size={20}/> : <><Save size={20}/> CRIAR CUPOM</>}
                 </button>
              </div>
           </form>
        </div>
      )}

      {/* MODAL OVERRIDE CABINET */}
      {editingCabinet && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-navy-950/80 backdrop-blur-md animate-in fade-in">
           <form onSubmit={handleUpdateCabinetPlan} className="bg-white dark:bg-navy-900 w-full max-w-lg rounded-[32px] shadow-2xl p-10 border border-neutral-200 dark:border-navy-800">
              <div className="flex justify-between items-center mb-8">
                <div>
                    <h3 className="text-2xl font-black text-navy-950 dark:text-white tracking-tight">Override de Gabinete</h3>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">{editingCabinet.name}</p>
                </div>
                <button type="button" onClick={() => setEditingCabinet(null)} className="p-2 text-slate-400 hover:bg-slate-50 rounded-full"><X size={24}/></button>
              </div>
              <div className="space-y-6">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">E-mail Administrativo (Override)</label>
                    <input name="admin_email" defaultValue={editingCabinet.admin_email} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-2xl px-6 py-4 font-bold text-sm outline-none dark:text-white" />
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Plano</label>
                        <select name="plan" defaultValue={editingCabinet.plan} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-2xl px-4 py-4 font-bold text-sm outline-none dark:text-white">
                        <option value="essential">Essencial</option>
                        <option value="advanced">Avançado</option>
                        <option value="pro">Pro</option>
                        <option value="betatester">Beta Tester</option>
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Status</label>
                        <select name="status" defaultValue={editingCabinet.status} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-2xl px-4 py-4 font-bold text-sm outline-none dark:text-white">
                        <option value="active">Ativo</option>
                        <option value="suspended">Suspenso</option>
                        <option value="pending">Pendente</option>
                        </select>
                    </div>
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Ciclo de Cobrança</label>
                    <select name="billing_cycle" defaultValue={editingCabinet.billing_cycle} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-700 rounded-2xl px-4 py-4 font-bold text-sm outline-none dark:text-white">
                    <option value="monthly">Mensal</option>
                    <option value="yearly">Anual</option>
                    </select>
                 </div>
                 <button disabled={loading} type="submit" className="w-full py-5 bg-navy-950 dark:bg-parliament-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl flex items-center justify-center gap-2">
                    {loading ? <Loader2 className="animate-spin" size={20}/> : <><Save size={20}/> SALVAR CONFIGURAÇÕES</>}
                 </button>
              </div>
           </form>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
        <div>
            <h1 className="text-5xl font-black tracking-tighter mb-2 text-navy-950 dark:text-white">Global Admin</h1>
            <p className="text-neutral-500 dark:text-slate-400 font-medium">Infraestrutura, privilégios e pagamentos pendentes.</p>
        </div>
        <div className="flex bg-neutral-100 dark:bg-navy-900 p-1.5 rounded-2xl border border-neutral-200 dark:border-navy-800 shadow-inner overflow-x-auto shrink-0 max-w-full">
            <button onClick={() => setActiveTab('instances')} className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'instances' ? 'bg-white shadow-lg dark:bg-navy-800 text-black dark:text-white' : 'text-neutral-400'}`}>Instâncias</button>
            <button onClick={() => setActiveTab('users')} className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeTab === 'users' ? 'bg-white shadow-lg dark:bg-navy-800 text-black dark:text-white' : 'text-neutral-400'}`}><Users size={14}/> Usuários</button>
            <button onClick={() => setActiveTab('support')} className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeTab === 'support' ? 'bg-white shadow-lg dark:bg-navy-800 text-black dark:text-white' : 'text-neutral-400'}`}><MessageSquare size={14}/> Suporte</button>
            <button onClick={() => setActiveTab('metrics')} className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeTab === 'metrics' ? 'bg-white shadow-lg dark:bg-navy-800 text-black dark:text-white' : 'text-neutral-400'}`}><BarChart3 size={14}/> Métricas</button>
            <button onClick={() => setActiveTab('requests')} className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeTab === 'requests' ? 'bg-white shadow-lg dark:bg-navy-800 text-black dark:text-white' : 'text-neutral-400'}`}><UserPlus size={14}/> Fila</button>
            <button onClick={() => setActiveTab('overrides')} className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeTab === 'overrides' ? 'bg-white shadow-lg dark:bg-navy-800 text-black dark:text-white' : 'text-neutral-400'}`}><Zap size={14}/> Over</button>
            <button onClick={() => setActiveTab('coupons')} className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeTab === 'coupons' ? 'bg-white shadow-lg dark:bg-navy-800 text-black dark:text-white' : 'text-neutral-400'}`}><Ticket size={14}/> Cupons</button>
        </div>
      </div>

      {/* Instances Tab */}
      {activeTab === 'instances' && (
        <div className="space-y-6 animate-in fade-in">
           <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-[32px] overflow-hidden shadow-sm">
              <table className="w-full text-left">
                 <thead>
                    <tr className="bg-neutral-50/50 dark:bg-navy-950/50 text-neutral-400 text-[9px] font-black uppercase tracking-widest border-b border-neutral-200 dark:border-navy-800">
                       <th className="px-8 py-5">Gabinete / Instância</th>
                       <th className="px-8 py-5">Plano / Billing</th>
                       <th className="px-8 py-5">Status</th>
                       <th className="px-8 py-5 text-right">Ações</th>
                    </tr>
                 </thead>
                 <tbody className="divide-y divide-neutral-100 dark:divide-navy-800">
                    {containers.map(c => (
                       <tr key={c.id} className="hover:bg-neutral-50 dark:hover:bg-navy-950 transition-colors">
                          <td className="px-8 py-6">
                             <p className="font-black text-navy-950 dark:text-white text-lg tracking-tighter uppercase">{c.name}</p>
                             <p className="text-[10px] font-bold text-slate-400 tracking-widest mt-0.5">{c.slug}.app</p>
                          </td>
                          <td className="px-8 py-6">
                             <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${c.plan === 'betatester' ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-600' : 'bg-parliament-50 dark:bg-navy-800 text-parliament-600'}`}>{PLAN_LIMITS[c.plan as keyof typeof PLAN_LIMITS]?.label || c.plan || 'essential'}</span>
                          </td>
                          <td className="px-8 py-6">
                             <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${c.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                                <span className={`text-[9px] font-black uppercase ${c.status === 'active' ? 'text-emerald-600' : 'text-red-600'}`}>{c.status === 'active' ? 'Ativo' : 'Suspenso'}</span>
                             </div>
                          </td>
                          <td className="px-8 py-6 text-right">
                             <div className="flex items-center justify-end gap-2">
                                <button onClick={() => onViewContainer(c.id)} className="p-2 text-slate-400 hover:text-navy-900 dark:hover:text-white transition-all flex items-center gap-1 font-black text-[8px] uppercase tracking-tighter"><Eye size={16}/> VER</button>
                                <button onClick={() => setEditingCabinet(c)} className="bg-neutral-100 dark:bg-navy-800 text-neutral-600 dark:text-slate-300 px-4 py-2 rounded-lg font-black text-[8px] uppercase tracking-widest hover:bg-parliament-600 hover:text-white transition-all">OVERRIDE</button>
                                <button onClick={() => handleToggleStatus(c.id, c.status || 'active')} disabled={actionLoading === c.id} className="p-2 text-slate-400 hover:text-amber-500 transition-all">
                                   {actionLoading === c.id ? <Loader2 size={18} className="animate-spin"/> : (c.status === 'active' ? <Pause size={18}/> : <PlayCircle size={18}/>)}
                                </button>
                                <button className="p-2 text-slate-300 hover:text-red-500 transition-all"><Trash2 size={18}/></button>
                             </div>
                          </td>
                       </tr>
                    ))}
                 </tbody>
              </table>
           </div>
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <div className="space-y-8 animate-in fade-in">
           <div className="p-6 bg-parliament-50 dark:bg-navy-950 border border-parliament-100 dark:border-navy-800 rounded-3xl mb-8 flex items-center gap-4">
              <div className="p-3 bg-white dark:bg-navy-800 rounded-2xl text-parliament-600 shadow-sm"><Info size={24}/></div>
              <p className="text-xs font-bold text-parliament-900 dark:text-parliament-300 leading-relaxed">
                 Fila de provisão pendente (Pagamentos em análise ou Overrides solicitados).
                 <br/>Ao ativar, a instância é criada e o usuário recebe acesso administrativo.
              </p>
           </div>
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pendingRequests.map(req => (
                 <div key={req.userId} className="bg-white dark:bg-navy-900 rounded-[32px] border border-neutral-200 dark:border-navy-800 p-8 shadow-sm hover:shadow-xl transition-all flex flex-col relative overflow-hidden">
                    {req.isOverride && <div className="absolute top-0 right-0 bg-parliament-600 text-white px-4 py-1 rounded-bl-xl text-[8px] font-black uppercase tracking-widest">Via Override</div>}
                    <div className="flex justify-between items-start mb-6">
                       <div className="p-3 bg-parliament-50 dark:bg-navy-800 text-parliament-600 rounded-2xl"><Building2 size={24}/></div>
                       <span className={`px-3 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest ${req.isOverride ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                          {req.isOverride ? 'Cortesia' : 'Aguardando Pagamento'}
                       </span>
                    </div>
                    <h3 className="text-xl font-black text-navy-950 dark:text-white mb-1">{req.cabinetName}</h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-6">{req.plan} • {req.slug}.app</p>
                    
                    <div className="space-y-3 mb-8 flex-1">
                       <div className="flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-300"><Users size={14}/> {req.userName}</div>
                       <div className="flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-300"><Mail size={14}/> {req.userEmail}</div>
                       <div className="flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-300"><Calendar size={14}/> {new Date(req.requestedAt).toLocaleDateString('pt-BR')}</div>
                    </div>

                    <button 
                      onClick={() => handleApproveCabinet(req)}
                      disabled={actionLoading === req.userId}
                      className="w-full py-4 bg-navy-950 dark:bg-parliament-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg flex items-center justify-center gap-2 hover:scale-[1.02] transition-all disabled:opacity-50"
                    >
                       {actionLoading === req.userId ? <Loader2 className="animate-spin" size={16}/> : <><Check size={16}/> {req.isOverride ? 'ATIVAR CORTESIA' : 'FORÇAR ATIVAÇÃO'}</>}
                    </button>
                 </div>
              ))}
              {pendingRequests.length === 0 && (
                 <div className="col-span-full py-24 text-center opacity-30">
                    <UserCheck size={48} className="mx-auto mb-4"/>
                    <p className="font-black uppercase tracking-widest text-xs">Nenhuma solicitação pendente</p>
                 </div>
              )}
           </div>
        </div>
      )}

      {activeTab === 'overrides' && (
        <div className="space-y-6 animate-in fade-in">
           <div className="flex justify-between items-center px-4">
              <div>
                  <h3 className="text-xl font-black text-navy-950 dark:text-white tracking-tighter">Overrides de E-mail</h3>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">E-mails autorizados a criar gabinete plano essencial sem cobrança inicial.</p>
              </div>
              <button onClick={() => setShowCreateOverride(true)} className="bg-navy-950 dark:bg-parliament-600 text-white px-8 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-xl flex items-center gap-2 hover:scale-[1.02] transition-all">
                <Plus size={16}/> Novo Override
              </button>
           </div>
           <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-[32px] overflow-hidden shadow-sm">
              <table className="w-full text-left">
                 <thead>
                    <tr className="bg-neutral-50/50 dark:bg-navy-950/50 text-neutral-400 text-[9px] font-black uppercase tracking-widest border-b border-neutral-200 dark:border-navy-800">
                       <th className="px-8 py-5">E-mail Autorizado</th>
                       <th className="px-8 py-5 text-center">Duração</th>
                       <th className="px-8 py-5">Data Concedido</th>
                       <th className="px-8 py-5 text-right">Ações</th>
                    </tr>
                 </thead>
                 <tbody className="divide-y divide-neutral-100 dark:divide-navy-800">
                    {emailOverrides.map(o => (
                       <tr key={o.id} className="hover:bg-neutral-50 dark:hover:bg-navy-950 transition-colors">
                          <td className="px-8 py-6">
                             <div className="flex items-center gap-3">
                                <div className="p-2 bg-amber-50 dark:bg-navy-800 text-amber-600 rounded-lg"><Mail size={16}/></div>
                                <span className="font-black text-navy-950 dark:text-white tracking-tight">{o.email}</span>
                             </div>
                          </td>
                          <td className="px-8 py-6 text-center">
                             <span className="px-3 py-1 bg-parliament-50 dark:bg-navy-800 text-parliament-600 rounded-lg text-[10px] font-black uppercase">{o.duration_days} dias</span>
                          </td>
                          <td className="px-8 py-6">
                             <p className="text-xs font-bold text-slate-400">{new Date(o.created_at).toLocaleDateString('pt-BR')}</p>
                          </td>
                          <td className="px-8 py-6 text-right">
                             <button onClick={() => handleDeleteOverride(o.id)} disabled={actionLoading === o.id} className="p-2 text-slate-300 hover:text-red-500 transition-all">
                                {actionLoading === o.id ? <Loader2 size={16} className="animate-spin"/> : <Trash2 size={18}/>}
                             </button>
                          </td>
                       </tr>
                    ))}
                    {emailOverrides.length === 0 && (
                      <tr><td colSpan={4} className="py-20 text-center opacity-30 text-xs font-black uppercase tracking-widest">Nenhum e-mail com override</td></tr>
                    )}
                 </tbody>
              </table>
           </div>
        </div>
      )}

      {activeTab === 'coupons' && (
        <div className="space-y-6 animate-in fade-in">
           <div className="flex justify-between items-center px-4">
              <div>
                  <h3 className="text-xl font-black text-navy-950 dark:text-white tracking-tighter">Gestão de Cupons</h3>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Cupons ativos para novos usuários.</p>
              </div>
              <button onClick={() => setShowCreateCoupon(true)} className="bg-navy-950 dark:bg-parliament-600 text-white px-8 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-xl flex items-center gap-2 hover:scale-[1.02] transition-all">
                <Plus size={16}/> Novo Cupom
              </button>
           </div>
           <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-[32px] overflow-hidden shadow-sm">
              <table className="w-full text-left">
                 <thead>
                    <tr className="bg-neutral-50/50 dark:bg-navy-950/50 text-neutral-400 text-[9px] font-black uppercase tracking-widest border-b border-neutral-200 dark:border-navy-800">
                       <th className="px-8 py-5">Código</th>
                       <th className="px-8 py-5">Valor</th>
                       <th className="px-8 py-5">Redenções</th>
                       <th className="px-8 py-5">Status</th>
                       <th className="px-8 py-5 text-right">Ações</th>
                    </tr>
                 </thead>
                 <tbody className="divide-y divide-neutral-100 dark:divide-navy-800">
                    {coupons.map(c => (
                       <tr key={c.id} className="hover:bg-neutral-50 dark:hover:bg-navy-950 transition-colors">
                          <td className="px-8 py-6">
                             <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${c.is_active ? 'bg-parliament-50 text-parliament-600' : 'bg-slate-100 text-slate-400'}`}><Ticket size={16}/></div>
                                <span className={`font-black uppercase tracking-widest ${c.is_active ? 'text-navy-950 dark:text-white' : 'text-slate-400 line-through'}`}>{c.code}</span>
                             </div>
                          </td>
                          <td className="px-8 py-6">
                             <span className="font-bold text-sm text-navy-900 dark:text-white">{c.type === 'percent' ? `${c.value}%` : `R$ ${c.value}`}</span>
                          </td>
                          <td className="px-8 py-6">
                             <p className="text-xs font-bold text-slate-600 dark:text-slate-400">{c._count_redemptions || 0} de {c.max_redemptions || '∞'}</p>
                          </td>
                          <td className="px-8 py-6">
                             <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border ${c.is_active ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                                {c.is_active ? 'Ativo' : 'Pausado'}
                             </span>
                          </td>
                          <td className="px-8 py-6 text-right">
                             <div className="flex items-center justify-end gap-2">
                                <button onClick={() => handleToggleCoupon(c.id, c.is_active)} disabled={actionLoading === c.id} className={`p-2 rounded-lg transition-all ${c.is_active ? 'text-amber-500 hover:bg-amber-50' : 'text-emerald-500 hover:bg-emerald-50'}`}>
                                   {actionLoading === c.id ? <Loader2 className="animate-spin" size={18}/> : (c.is_active ? <Pause size={18}/> : <PlayCircle size={18}/>)}
                                </button>
                                <button onClick={() => handleDeleteCoupon(c.id)} disabled={actionLoading === c.id} className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all">
                                   {actionLoading === c.id ? <Loader2 className="animate-spin" size={18}/> : <Trash2 size={18}/>}
                                </button>
                             </div>
                          </td>
                       </tr>
                    ))}
                    {coupons.length === 0 && (
                      <tr><td colSpan={5} className="py-20 text-center opacity-30 text-xs font-black uppercase tracking-widest">Nenhum cupom cadastrado</td></tr>
                    )}
                 </tbody>
              </table>
           </div>
        </div>
      )}

      {/* Support Tab */}
      {activeTab === 'support' && <SuperAdminSupportPanel />}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6 animate-in fade-in">
           <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-[32px] overflow-hidden shadow-sm">
              <table className="w-full text-left">
                 <thead className="bg-neutral-50/50 dark:bg-navy-950/50 border-b border-neutral-200 dark:border-navy-800">
                    <tr>
                       <th className="px-8 py-5 text-[9px] font-black uppercase tracking-widest text-slate-400">Usuário</th>
                       <th className="px-8 py-5 text-[9px] font-black uppercase tracking-widest text-slate-400">Função</th>
                       <th className="px-8 py-5 text-[9px] font-black uppercase tracking-widest text-slate-400">Gabinete Associado</th>
                    </tr>
                 </thead>
                 <tbody className="divide-y divide-neutral-100 dark:divide-navy-800">
                    {allUsers.map(user => (
                       <tr key={user.id} className="hover:bg-neutral-50 dark:hover:bg-navy-950 transition-colors">
                          <td className="px-8 py-6">
                             <p className="text-sm font-bold text-navy-900 dark:text-white">{user.name}</p>
                             <p className="text-xs text-slate-400">{user.email}</p>
                             {user.phone && <p className="text-xs text-slate-400">{user.phone}</p>}
                          </td>
                          <td className="px-8 py-6">
                             <span className="bg-slate-100 dark:bg-navy-800 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-300">
                                {user.role}
                             </span>
                          </td>
                          <td className="px-8 py-6">
                             {user.containers ? (
                                <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{user.containers.name}</span>
                             ) : (
                                <span className="text-xs text-slate-400 italic">Sem Gabinete</span>
                             )}
                          </td>
                       </tr>
                    ))}
                 </tbody>
              </table>
           </div>
        </div>
      )}

      {/* Metrics Tab */}
      {activeTab === 'metrics' && (
        <div className="space-y-6 animate-in fade-in">
           <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-[32px] overflow-hidden shadow-sm">
              <table className="w-full text-left">
                 <thead className="bg-neutral-50/50 dark:bg-navy-950/50 border-b border-neutral-200 dark:border-navy-800">
                    <tr>
                       <th className="px-8 py-5 text-[9px] font-black uppercase tracking-widest text-slate-400">Gabinete</th>
                       <th className="px-8 py-5 text-[9px] font-black uppercase tracking-widest text-slate-400 text-center">Contatos (CRM)</th>
                       <th className="px-8 py-5 text-[9px] font-black uppercase tracking-widest text-slate-400 text-center">Usuários (Equipe)</th>
                       <th className="px-8 py-5 text-[9px] font-black uppercase tracking-widest text-slate-400 text-center">Armazenamento</th>
                    </tr>
                 </thead>
                 <tbody className="divide-y divide-neutral-100 dark:divide-navy-800">
                    {containerMetrics.map(m => (
                       <tr key={m.container_id} className="hover:bg-neutral-50 dark:hover:bg-navy-950 transition-colors">
                          <td className="px-8 py-6">
                             <p className="text-sm font-black text-navy-900 dark:text-white uppercase">{m.container_name}</p>
                          </td>
                          <td className="px-8 py-6 text-center">
                             <span className="text-lg font-bold text-emerald-600">{m.contacts_count}</span>
                          </td>
                          <td className="px-8 py-6 text-center">
                             <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{m.users_count || 0}</span>
                          </td>
                          <td className="px-8 py-6 text-center">
                             <div className="flex flex-col items-center">
                                <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                                   {(m.storage_used_bytes / (1024 * 1024)).toFixed(2)} MB
                                </span>
                                {/* TODO: Implementar lógica real de cálculo de storage quando a tabela container_storage_metrics for populada via edge functions/triggers */}
                                <span className="text-[8px] text-slate-400 uppercase tracking-widest mt-1">Estimado</span>
                             </div>
                          </td>
                       </tr>
                    ))}
                 </tbody>
              </table>
           </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;